"use client";

import { useId } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "~/lib/utils";

type DotConfig = {
    cx: number;
    cy: number;
    r: number;
    ry?: number;
    delay: number;
};

export type AnimatedLineConfig = {
    viewBox: string;
    path: string;
    dashArray?: string;
    dots: DotConfig[];
    gradient: { x: number; y1: number; y2: number };
};

type AnimatedLineProps = AnimatedLineConfig & {
    className?: string;
};

const REVEAL_DURATION = 1.2;
const DOT_FADE_DURATION = 0.4;

const AnimatedLine = ({
    viewBox,
    path,
    dashArray = "4 4",
    dots,
    gradient,
    className,
}: AnimatedLineProps) => {
    const uid = useId();
    const clipId = `${uid}-clip`;
    const gradientId = `${uid}-linear`;
    const prefersReducedMotion = useReducedMotion();

    const revealDuration = prefersReducedMotion ? 0 : REVEAL_DURATION;
    const [, , vbWidth, vbHeight] = viewBox.split(" ");

    return (
        <svg
            viewBox={viewBox}
            fill="none"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
            focusable="false"
            className={cn("h-full w-auto", className)}
        >
            <defs>
                <clipPath id={clipId}>
                    <motion.rect
                        x="0"
                        y="0"
                        height={vbHeight}
                        initial={{ width: 0 }}
                        animate={{ width: Number(vbWidth) }}
                        transition={{ duration: revealDuration, ease: "easeOut" }}
                    />
                </clipPath>
                <linearGradient id={gradientId} x1={gradient.x} y1={gradient.y1} x2={gradient.x} y2={gradient.y2} gradientUnits="userSpaceOnUse">
                    <stop stopColor="#5472CC" />
                    <stop offset="1" stopColor="white" />
                </linearGradient>
                {dots.map((dot, i) => {
                    const radialId = `${uid}-radial${i}`;
                    const isEllipse = dot.ry != null;
                    return (
                        <radialGradient
                            key={radialId}
                            id={radialId}
                            cx="0"
                            cy="0"
                            r="1"
                            gradientUnits="userSpaceOnUse"
                            gradientTransform={`translate(${dot.cx} ${dot.cy}) rotate(90) scale(${isEllipse ? `${dot.ry} ${dot.r}` : dot.r})`}
                        >
                            <stop stopColor="#5472CC" />
                            <stop offset="0.697115" stopColor="#5472CC" stopOpacity="0.15" />
                        </radialGradient>
                    );
                })}
            </defs>

            <g clipPath={`url(#${clipId})`}>
                <path d={path} stroke="white" strokeDasharray={dashArray} />
                <path d={path} stroke={`url(#${gradientId})`} strokeDasharray={dashArray} />
            </g>

            {dots.map((dot, i) => {
                const radialId = `${uid}-radial${i}`;
                const delay = prefersReducedMotion ? 0 : dot.delay;
                const isEllipse = dot.ry != null;

                return isEllipse ? (
                    <motion.ellipse
                        key={radialId}
                        cx={dot.cx}
                        cy={dot.cy}
                        rx={dot.r}
                        ry={dot.ry}
                        fill={`url(#${radialId})`}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: DOT_FADE_DURATION, delay }}
                    />
                ) : (
                    <motion.circle
                        key={radialId}
                        cx={dot.cx}
                        cy={dot.cy}
                        r={dot.r}
                        fill={`url(#${radialId})`}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: DOT_FADE_DURATION, delay }}
                    />
                );
            })}
        </svg>
    );
};

export default AnimatedLine;
